/**
 * Hook customizado para gerenciar dados do Dashboard
 * Centraliza a lógica de busca, estados e cache de dados
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import dashboardService from '../services/dashboardService';

const useDashboardData = (options = {}) => {
  const {
    autoFetch = true,
    cacheTime = 5 * 60 * 1000, // 5 minutos de cache
    cityId = null, // ID da cidade para filtrar (usado por admins)
  } = options;

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [lastCityId, setLastCityId] = useState(null);

  // Dados brutos
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [idebIndicators, setIdebIndicators] = useState([]);
  const [flowIndicators, setFlowIndicators] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [geolocations, setGeolocations] = useState([]);

  // Estados de paginação para tabela de alunos
  const [pagedStudents, setPagedStudents] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [studentTotalPages, setStudentTotalPages] = useState(1);
  const [studentCount, setStudentCount] = useState(0);

  const ITEMS_PER_PAGE = 10;

  /**
   * Busca todos os dados do dashboard
   */
  const fetchData = useCallback(async (forceRefresh = false) => {
    // Cache: so reaproveita se for a mesma cidade dentro do TTL
    const sameCity = lastCityId === cityId;
    if (!forceRefresh && sameCity && lastFetch && Date.now() - lastFetch < cacheTime) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await dashboardService.fetchDashboardData(cityId);

      setStudents(data.students);
      setClasses(data.classes);
      setTeachers(data.teachers);
      setSchools(data.schools);
      setIdebIndicators(data.idebIndicators);
      setFlowIndicators(data.flowIndicators);
      setAlerts(data.alerts);
      setGeolocations(data.geolocations || []);

      // Configura paginação de alunos
      setStudentCount(data.students.length);
      setStudentTotalPages(Math.ceil(data.students.length / ITEMS_PER_PAGE));
      setPagedStudents(data.students.slice(0, ITEMS_PER_PAGE));

      setLastFetch(Date.now());
      setLastCityId(cityId);
    } catch (err) {
      console.error('Erro ao buscar dados do dashboard:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [cacheTime, lastFetch, lastCityId, cityId]);

  /**
   * Busca página específica de alunos
   */
  const fetchStudentPage = useCallback((page) => {
    setStudentPage(page);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPagedStudents(students.slice(startIndex, endIndex));
  }, [students]);

  // Atualiza página quando muda
  useEffect(() => {
    if (students.length > 0) {
      fetchStudentPage(studentPage);
    }
  }, [studentPage, students, fetchStudentPage]);

  // Fetch inicial automático
  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchData]);

  // Re-fetch sempre que a cidade mudar (inclusive para null = Todas)
  useEffect(() => {
    fetchData(true);
  }, [cityId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Estatísticas calculadas (memoizadas)
   */
  const stats = useMemo(() => {
    // Retorna null apenas se estiver carregando
    // Permite calcular stats mesmo sem alunos (para mostrar escolas, turmas, etc.)
    if (loading) {
      return null;
    }

    // Se não houver nenhum dado, retorna null
    if (!students.length && !schools.length && !classes.length && !teachers.length) {
      return null;
    }

    return dashboardService.calculateDashboardStats({
      students,
      classes,
      teachers,
      schools,
      idebIndicators,
      flowIndicators,
      geolocations,
    });
  }, [students, classes, teachers, schools, idebIndicators, flowIndicators, geolocations, loading]);

  /**
   * Métricas formatadas para os cards
   */
  const metrics = useMemo(() => {
    if (!stats) return [];

    const schoolsByZone = stats.charts.schoolsByZone;
    const urbanas = schoolsByZone.find(z => z.name === 'Urbana')?.value || 0;
    const rurais = schoolsByZone.find(z => z.name === 'Rural')?.value || 0;

    return [
      {
        label: 'Total de Escolas',
        value: stats.totals.schools.toString(),
        subtitle: `${urbanas} urbanas - ${rurais} rurais`,
        color: 'blue'
      },
      {
        label: 'Total de Alunos',
        value: stats.totals.students.toLocaleString('pt-BR'),
        subtitle: `${stats.totals.activeStudents.toLocaleString('pt-BR')} ativos`,
        color: 'green'
      },
      {
        label: 'IDEB Médio',
        value: stats.ideb.average ? stats.ideb.average.toFixed(1) : 'N/D',
        subtitle: stats.ideb.average
          ? `${stats.ideb.aboveTarget} escolas acima da meta`
          : 'Dados não disponíveis',
        color: 'purple'
      },
      {
        label: 'Taxa de Aprovação',
        value: stats.approvalRate ? `${stats.approvalRate.toFixed(1)}%` : 'N/D',
        subtitle: stats.approvalRate
          ? 'Média das escolas'
          : 'Dados não disponíveis',
        color: 'orange'
      },
    ];
  }, [stats]);

  /**
   * Alertas formatados
   */
  const formattedAlerts = useMemo(() => {
    if (!stats) {
      return [
        { icon: '📊', title: 'Carregando dados...', desc: 'Aguarde', type: 'info' },
      ];
    }

    const alertsList = [];

    // Alerta de escolas acima da meta
    if (stats.ideb.aboveTarget > 0) {
      alertsList.push({
        icon: '🏆',
        title: `${stats.ideb.aboveTarget} escolas acima da meta`,
        desc: 'Desempenho excelente no IDEB',
        type: 'success'
      });
    }

    // Alerta de escolas abaixo da meta
    if (stats.ideb.belowTarget > 0) {
      alertsList.push({
        icon: '📉',
        title: `${stats.ideb.belowTarget} escolas abaixo da meta`,
        desc: 'Requerem atenção especial',
        type: stats.ideb.belowTarget > 3 ? 'danger' : 'warning'
      });
    }

    // Alerta de turmas
    if (stats.totals.classes > 0) {
      alertsList.push({
        icon: '📚',
        title: `${stats.totals.classes} turmas ativas`,
        desc: `${stats.totals.teachers} professores atuando`,
        type: 'success'
      });
    }

    // Se não houver alertas específicos, mostra resumo
    if (alertsList.length === 0) {
      alertsList.push({
        icon: '✅',
        title: 'Sistema operando normalmente',
        desc: 'Todos os indicadores estáveis',
        type: 'success'
      });
    }

    return alertsList.slice(0, 3); // Limita a 3 alertas
  }, [stats]);

  /**
   * Dados para gráficos
   */
  const chartData = useMemo(() => {
    if (!stats) {
      return {
        studentsByClass: [],
        studentsByStatus: [],
        classesByGrade: [],
        studentsBySchool: [],
        schoolsByZone: [],
      };
    }

    return {
      studentsByClass: stats.charts.studentsByClass.slice(0, 10), // Top 10 turmas
      studentsByStatus: stats.charts.studentsByStatus,
      classesByGrade: stats.charts.classesByGrade,
      studentsBySchool: stats.charts.studentsBySchool.slice(0, 10), // Top 10 escolas
      schoolsByZone: stats.charts.schoolsByZone,
    };
  }, [stats]);

  /**
   * Dados de evolução IDEB por ano (para gráfico de linha)
   */
  const idebEvolution = useMemo(() => {
    // Retorna array vazio se não houver dados reais
    if (idebIndicators.length === 0) {
      return [];
    }

    // Agrupa por ano fiscal
    const byYear = idebIndicators.reduce((acc, ind) => {
      const year = ind.fiscal_year.toString();
      if (!acc[year]) {
        acc[year] = { scores: [], targets: [] };
      }
      if (ind.actual_avg_score) {
        acc[year].scores.push(parseFloat(ind.actual_avg_score));
      }
      if (ind.ideb_target) {
        acc[year].targets.push(parseFloat(ind.ideb_target));
      }
      return acc;
    }, {});

    return Object.entries(byYear)
      .map(([year, data]) => ({
        year,
        ideb: data.scores.length > 0
          ? data.scores.reduce((a, b) => a + b, 0) / data.scores.length
          : null,
        meta: data.targets.length > 0
          ? data.targets.reduce((a, b) => a + b, 0) / data.targets.length
          : null,
      }))
      .filter(d => d.ideb !== null)
      .sort((a, b) => a.year.localeCompare(b.year));
  }, [idebIndicators]);

  /**
   * Top escolas por IDEB
   */
  const topSchoolsByIdeb = useMemo(() => {
    // Retorna array vazio se não houver dados reais
    if (idebIndicators.length === 0 || schools.length === 0) {
      return [];
    }

    return idebIndicators
      .filter(ind => ind.actual_avg_score)
      .map(ind => {
        const school = schools.find(s => s.id === ind.id_school);
        return {
          name: school?.school || 'Escola não encontrada',
          ideb: parseFloat(ind.actual_avg_score),
        };
      })
      .sort((a, b) => b.ideb - a.ideb)
      .slice(0, 5);
  }, [idebIndicators, schools]);

  return {
    // Estados
    loading,
    error,

    // Dados brutos
    students,
    classes,
    teachers,
    schools,
    idebIndicators,
    flowIndicators,
    alerts,

    // Dados paginados
    pagedStudents,
    studentPage,
    studentTotalPages,
    studentCount,

    // Estatísticas calculadas
    stats,
    metrics,
    formattedAlerts,
    chartData,
    idebEvolution,
    topSchoolsByIdeb,

    // Ações
    fetchData,
    fetchStudentPage,
    setStudentPage,
    refresh: () => fetchData(true),
  };
};

export default useDashboardData;
